import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL, formatError } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

const KEY = "tga_chat";
const chatApi = axios.create({ baseURL: `${BACKEND_URL}/api/chat` });
const load = () => { try { return JSON.parse(localStorage.getItem(KEY)); } catch { return null; } };

const Bubble = ({ m }) => (
  <div className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
    <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-line ${m.sender === "visitor" ? "bg-emerald-500 text-[#151827] rounded-br-sm" : "bg-[#2c3149] text-white rounded-bl-sm"}`}>
      {m.text}
      <p className={`text-[10px] mt-1 ${m.sender === "visitor" ? "text-[#151827]/60" : "text-zinc-400"}`}>{new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</p>
    </div>
  </div>
);

const StartForm = ({ onStarted }) => {
  const [f, setF] = useState({ name: "", email: "", text: "", website: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await chatApi.post("/start", { ...f, email: f.email || null });
      localStorage.setItem(KEY, JSON.stringify(data)); onStarted(data);
    } catch (err) { toast.error(formatError(err)); } finally { setBusy(false); }
  };
  const cls = "w-full rounded-xl bg-[#262b42] border border-white/15 px-3 py-2.5 text-sm outline-none focus:border-emerald-400";
  return (
    <form onSubmit={submit} className="p-4 space-y-3" data-testid="chat-start-form">
      <p className="text-sm text-zinc-300">Scrivici: ti risponderemo qui in chat.</p>
      <input required maxLength={80} placeholder="Il tuo nome" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={cls} data-testid="chat-name-input" />
      <input type="email" placeholder="Email (facoltativa)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={cls} data-testid="chat-email-input" />
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="hidden" aria-hidden="true" />
      <textarea required maxLength={2000} rows={3} placeholder="Il tuo messaggio" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={cls} data-testid="chat-first-message-input" />
      <button disabled={busy} className="w-full rounded-full bg-emerald-500 text-[#151827] py-2.5 font-bold hover:bg-emerald-400 disabled:opacity-60" data-testid="chat-start-btn">{busy ? "Invio…" : "Inizia la chat"}</button>
    </form>
  );
};

const Conversation = ({ chat, onLost }) => {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef();
  const headers = { "X-Chat-Token": chat.token };
  const refresh = () => chatApi.get(`/${chat.chat_id}`, { headers }).then((r) => setMsgs(r.data.messages)).catch((e) => { if (e?.response?.status === 404) onLost(); });
  useEffect(() => { refresh(); const t = setInterval(refresh, 5000); return () => clearInterval(t); }, [chat.chat_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { const { data } = await chatApi.post(`/${chat.chat_id}/messages`, { text }, { headers }); setMsgs((m) => [...m, data]); setText(""); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="chat-messages">
        {msgs.map((m) => <Bubble key={m.id} m={m} />)}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-white/10 flex gap-2">
        <input value={text} maxLength={2000} onChange={(e) => setText(e.target.value)} placeholder="Scrivi un messaggio…" className="flex-1 rounded-full bg-[#262b42] border border-white/15 px-4 text-sm outline-none focus:border-emerald-400" data-testid="chat-message-input" />
        <button className="h-10 w-10 rounded-full bg-emerald-500 text-[#151827] grid place-items-center hover:scale-105 transition-transform" aria-label="Invia" data-testid="chat-send-btn"><Send className="h-4 w-4" /></button>
      </form>
    </>
  );
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState(load);
  const { current } = usePlayer();
  const lost = () => { localStorage.removeItem(KEY); setChat(null); };
  return (
    <div className={`fixed right-4 z-50 ${current ? "bottom-24" : "bottom-6"} transition-[bottom] duration-300`}>
      {open && (
        <div className="mb-3 w-[min(92vw,360px)] h-[480px] max-h-[70vh] flex flex-col rounded-3xl cdj-body border border-white/15 overflow-hidden rise" data-testid="chat-panel">
          <div className="flex items-center justify-between px-4 py-3 bg-emerald-500 text-[#151827]">
            <div><p className="font-display font-black text-sm uppercase">Chat con l'etichetta</p><p className="text-[11px] font-semibold">The Groove Anomaly records</p></div>
            <button onClick={() => setOpen(false)} aria-label="Chiudi chat" data-testid="chat-close-btn"><X className="h-5 w-5" /></button>
          </div>
          {chat ? <Conversation chat={chat} onLost={lost} /> : <StartForm onStarted={setChat} />}
        </div>
      )}
      <button onClick={() => setOpen(!open)} aria-label="Apri chat" data-testid="chat-toggle-btn"
        className="ml-auto h-14 w-14 rounded-full bg-emerald-500 text-[#151827] grid place-items-center shadow-[0_10px_30px_rgba(31,232,150,0.5)] hover:scale-110 transition-transform glow-pulse">
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>
    </div>
  );
};
